import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Monitor, Smartphone } from "lucide-react";
import { Button } from "@/components/ui/button";

export type CustomerEmailType =
  | "welcome"
  | "invited"
  | "initial-sync-google"
  | "initial-sync-microsoft"
  | "salesforce-sync"
  | "hubspot-sync"
  | "stripe-sync"
  | "quickbooks-sync"
  | "sync-stopped-google"
  | "sync-stopped-microsoft"
  | "sync-stopped-zoom"
  | "sync-stopped-salesforce"
  | "sync-stopped-hubspot"
  | "sync-stopped-stripe"
  | "sync-stopped-quickbooks"
  | "first-meeting"
  | "video-viewed"
  | "aws-downtime"
  | "service-restored"
  | "pipeline-changes"
  | "pipeline-cleanup"
  | "daily-prep"
  | "daily-followups"
  | "changelog"
  | "changelog-may"
  | "changelog-june"
  | "changelog-july"
  | "changelog-august"
  | "not-synced-google"
  | "not-synced-microsoft"
  | "no-meeting-recording"
  | "no-deals";

interface DealSection {
  title: string;
  deals: { company: string; companyLogo?: string; dealName: string; dealUrl?: string }[];
}

interface MeetingItem {
  time: string;
  company: string;
  companyLogo?: string;
  briefingUrl: string;
}

interface TaskItem {
  title: string;
  taskUrl: string;
}

interface PrepSection {
  title: string;
  meetings?: MeetingItem[];
  tasks?: TaskItem[];
}

interface FollowUpMeeting {
  company: string;
  companyLogo?: string;
  followUpUrl: string;
}

interface SuggestedTask {
  title: string;
  addUrl: string;
}

interface SuggestedDealUpdate {
  label: string;
  actionLabel: string;
  actionUrl: string;
  isNew?: boolean;
}

interface FollowUpSection {
  title: string;
  followUpMeetings?: FollowUpMeeting[];
  suggestedTasks?: SuggestedTask[];
  suggestedDealUpdates?: SuggestedDealUpdate[];
}

interface EmailMeta {
  subject: string;
  preheader: string;
  heading: string;
  body: string;
  ctaLabel?: string;
  ctaUrl?: string;
  icon?: string;
  /** Emoji displayed below the Coffee logo in the header */
  emoji?: string;
  /** Optional structured table to render after the body text */
  table?: { label: string; value: string | number }[];
  /** Optional deal sections for pipeline cleanup style emails */
  dealSections?: DealSection[];
  /** Optional prep sections for daily prep style emails */
  prepSections?: PrepSection[];
  /** Optional follow-up sections for daily wrap-up style emails */
  followUpSections?: FollowUpSection[];
  /** Optional footer link */
  footerLink?: { label: string; url: string };
}

const EMAIL_DATA: Record<CustomerEmailType, EmailMeta> = {
  welcome: {
    subject: "Welcome to Coffee ☕",
    preheader: "We've been brewing something special for you.",
    heading: "Welcome to Coffee",
    emoji: "👋",
    body: "Hi {{first_name}},\n\nWelcome to Coffee. We've been brewing something special for you.\n\nOnce you've synced your email and calendar account, Coffee will:\n\n• Automatically create company and contact records. Coffee will create records for all the external parties you meet with or email with, minus the spam.\n• Enrich all your records. No need to buy third-party data. Coffee has company and contact enrichment built in.\n• Take notes on all your video conference calls. Coffee meeting bot will sit in your external meetings, transcribe each call, and create all the follow-up materials and detailed notes.\n• Keep track of all data changes. Coffee is built on a customer data platform that never throws away data. Easily compare weekly pipeline changes.\n• Let you ask questions in natural language. The Ask Coffee feature lets you prompt Coffee just like you were using ChatGPT.\n\nIf you need any assistance or want to schedule a product demo, please feel free to reach out to us at [support@coffee.ai](mailto:support@coffee.ai).\n\nWe're so happy you're here and hope you find Coffee delicious 😋.\n\nBest,\nThe Coffee Team\n\nP.S. To find out about new Coffee updates first, just [follow us on LinkedIn](https://linkedin.com/company/coffeecrm/).",
    ctaLabel: "Open Coffee",
    ctaUrl: "https://app.coffee.com",
  },
  invited: {
    subject: "You've been invited to Coffee ☕",
    preheader: "{{inviter_name}} invited you to join their workspace.",
    heading: "You're invited",
    emoji: "🤝",
    body: "Hi {{first_name}},\n\n{{inviter_name}} has invited you to join the {{workspace_name}} workspace on Coffee.\n\nCoffee is your personal AI agent that takes away all the busy work in updating your CRM.",
    ctaLabel: "Accept Invitation",
    ctaUrl: "https://app.coffee.com/invite",
  },
  "initial-sync-google": {
    subject: "We've synced your Google Workspace account to Coffee",
    preheader: "Your initial email and calendar sync is complete.",
    heading: "Initial Sync Complete",
    body: "",
    icon: "/logos/google.svg",
    ctaLabel: "Open Coffee",
    ctaUrl: "https://app.coffee.com",
    table: [
      { label: "Contacts created", value: "847" },
      { label: "Contacts enriched", value: "812" },
      { label: "Companies created", value: "314" },
      { label: "Companies enriched", value: "298" },
      { label: "Meetings attached", value: "1,243" },
      { label: "Emails attached", value: "4,718" },
    ],
  },
  "initial-sync-microsoft": {
    subject: "We've synced your Microsoft 365 account to Coffee",
    preheader: "Your initial email and calendar sync is complete.",
    heading: "Initial Sync Complete",
    body: "",
    icon: "/logos/microsoft.svg",
    ctaLabel: "Open Coffee",
    ctaUrl: "https://app.coffee.com",
    table: [
      { label: "Contacts created", value: "847" },
      { label: "Contacts enriched", value: "812" },
      { label: "Companies created", value: "314" },
      { label: "Companies enriched", value: "298" },
      { label: "Meetings attached", value: "1,243" },
      { label: "Emails attached", value: "4,718" },
    ],
  },
  "salesforce-sync": {
    subject: "Your Salesforce sync is complete",
    preheader: "Your initial Salesforce sync is now complete.",
    heading: "Salesforce Sync Complete",
    body: "Hi {{first_name}},\n\nGreat news — your initial Salesforce sync is now complete. Coffee will keep your contacts, companies, and deals in sync automatically.",
    ctaLabel: "View Sync Settings",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/salesforce-color.svg",
  },
  "hubspot-sync": {
    subject: "Your HubSpot sync is complete",
    preheader: "Your initial HubSpot sync is now complete.",
    heading: "HubSpot Sync Complete",
    body: "Hi {{first_name}},\n\nGreat news — your initial HubSpot sync is now complete. Coffee will keep your contacts, companies, and deals in sync automatically.",
    ctaLabel: "View Sync Settings",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/hubspot-color.svg",
  },
  "stripe-sync": {
    subject: "Your Stripe sync is complete",
    preheader: "Your initial Stripe sync is now complete.",
    heading: "Stripe Sync Complete",
    body: "Hi {{first_name}},\n\nGreat news — your initial Stripe sync is now complete. Coffee will keep your customer and payment data in sync automatically.",
    ctaLabel: "View Sync Settings",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/stripe.png",
  },
  "quickbooks-sync": {
    subject: "Your QuickBooks sync is complete",
    preheader: "Your initial QuickBooks sync is now complete.",
    heading: "QuickBooks Sync Complete",
    body: "Hi {{first_name}},\n\nGreat news — your initial QuickBooks sync is now complete. Coffee will keep your customer and payment data in sync automatically.",
    ctaLabel: "View Sync Settings",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/quickbooks.svg",
  },
  "sync-stopped-google": {
    subject: "Your Google Workspace sync has stopped",
    preheader: "Action required: reconnect your Google account.",
    heading: "Google Workspace Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your Google Workspace sync has stopped working. This means Coffee can no longer access your calendar and email.\n\nThis usually happens when your authorization expires or permissions change. Please reconnect your account to resume syncing.",
    ctaLabel: "Reconnect Google",
    ctaUrl: "https://app.coffee.com/settings?section=accounts",
    icon: "/logos/google.svg",
  },
  "sync-stopped-microsoft": {
    subject: "Your Microsoft 365 sync has stopped",
    preheader: "Action required: reconnect your Microsoft account.",
    heading: "Microsoft 365 Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your Microsoft 365 sync has stopped working. This means Coffee can no longer access your calendar and email.\n\nThis usually happens when your authorization expires or permissions change. Please reconnect your account to resume syncing.",
    ctaLabel: "Reconnect Microsoft",
    ctaUrl: "https://app.coffee.com/settings?section=accounts",
    icon: "/logos/microsoft.svg",
  },
  "sync-stopped-zoom": {
    subject: "Your Zoom sync has stopped",
    preheader: "Action required: reconnect your Zoom account.",
    heading: "Zoom Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your Zoom sync has stopped working.\n\nThis means that the Coffee meeting bot will not automatically be added to meetings and must be added manually each time.\n\nPlease reconnect your account.",
    ctaLabel: "Reconnect Zoom",
    ctaUrl: "https://app.coffee.com/settings?section=accounts",
    icon: "/logos/zoom.svg",
  },
  "sync-stopped-salesforce": {
    subject: "Your Salesforce sync has stopped",
    preheader: "Action required: reconnect your Salesforce org.",
    heading: "Salesforce Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your Salesforce sync has stopped working.\n\nPlease reconnect your Salesforce account to resume syncing.",
    ctaLabel: "Reconnect Salesforce",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/salesforce-color.svg",
  },
  "sync-stopped-hubspot": {
    subject: "Your HubSpot sync has stopped",
    preheader: "Action required: reconnect your HubSpot account.",
    heading: "HubSpot Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your HubSpot sync has stopped working.\n\nPlease reconnect your HubSpot account to resume syncing.",
    ctaLabel: "Reconnect HubSpot",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/hubspot-color.svg",
  },
  "sync-stopped-stripe": {
    subject: "Your Stripe sync has stopped",
    preheader: "Action required: reconnect your Stripe account.",
    heading: "Stripe Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your Stripe sync has stopped working.\n\nPlease reconnect your Stripe account to resume syncing.",
    ctaLabel: "Reconnect Stripe",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/stripe.png",
  },
  "sync-stopped-quickbooks": {
    subject: "Your QuickBooks sync has stopped",
    preheader: "Action required: reconnect your QuickBooks account.",
    heading: "QuickBooks Sync Stopped",
    body: "Hi {{first_name}},\n\nWe noticed that your QuickBooks sync has stopped working.\n\nPlease reconnect your QuickBooks account to resume syncing.",
    ctaLabel: "Reconnect QuickBooks",
    ctaUrl: "https://app.coffee.com/settings?section=integrations",
    icon: "/logos/quickbooks.svg",
  },
  "first-meeting": {
    subject: "Coffee ☕ has processed your first meeting recording",
    preheader: "Coffee has processed your first meeting recording.",
    heading: "Congratulations!",
    emoji: "🎉",
    body: "Hi {{first_name}},\n\nYour first meeting — \"{{meeting_title}}\" — has been processed by Coffee. Here's everything we created for you:\n\n• 📋 [Meeting summary](https://app.coffee.com/meetings/{{meeting_id}}?tab=summary)\n• 📝 [Detailed notes](https://app.coffee.com/meetings/{{meeting_id}}?tab=notes)\n• 🎥 [Meeting recording & transcript](https://app.coffee.com/meetings/{{meeting_id}}?tab=recording)\n• ✉️ [Follow-up email draft](https://app.coffee.com/meetings/{{meeting_id}}?tab=email)\n• ✅ [Action item suggestions](https://app.coffee.com/meetings/{{meeting_id}}?tab=actions)",
    ctaLabel: "View Meeting",
    ctaUrl: "https://app.coffee.com/meetings/{{meeting_id}}",
  },
  "video-viewed": {
    subject: "Someone watched your recording",
    preheader: "{{viewer_name}} viewed your meeting recording.",
    heading: "Recording Viewed",
    emoji: "🎬",
    body: "Hi {{first_name}},\n\n{{viewer_name}} ({{viewer_email}}) watched the recording of your meeting:\n\n\"{{meeting_title}}\"",
    ctaLabel: "View Recording",
    ctaUrl: "https://app.coffee.com/meetings",
  },
  "aws-downtime": {
    subject: "Downtime impacting Coffee",
    preheader: "Coffee is experiencing a temporary outage.",
    heading: "Service Disruption",
    emoji: "🚧",
    body: "Hi {{first_name}},\n\nWe want to let you know that Coffee is currently experiencing a temporary outage due to an ongoing service disruption with one of our providers.\n\nOur team is actively monitoring the situation and working to restore full access as soon as possible. We will keep you updated and notify you once we are back online.\n\nThank you for your patience and we apologize for the inconvenience.\n\nThe Coffee Team\n\nIf you have any questions, please reach out to us at [support@coffee.ai](mailto:support@coffee.ai).",
  },
  "service-restored": {
    subject: "Coffee is pouring again ☕",
    preheader: "Coffee is back up and running.",
    heading: "Service Restored",
    emoji: "✅",
    body: "The service provider issue has been resolved and Coffee is back up and running. Thank you for your patience.\n\nThe Coffee Team",
  },
  "pipeline-cleanup": {
    subject: "Your Weekly Pipeline Tasks",
    preheader: "Start next week fresh — deals that may need attention.",
    heading: "Pipeline Cleanup",
    emoji: "🧹",
    body: "Hi Doug,\n\nHere are your deals that may need attention.",
    dealSections: [
      {
        title: "Close dates in the past",
        deals: [
          { company: "Stripe", companyLogo: "/logos/stripe.png", dealName: "Stripe Annual License", dealUrl: "https://app.coffee.com/deals/1" },
          { company: "Notion", companyLogo: "/logos/notion.svg", dealName: "Notion Team Expansion", dealUrl: "https://app.coffee.com/deals/2" },
          { company: "Figma", companyLogo: "/logos/figma.png", dealName: "Figma Enterprise", dealUrl: "https://app.coffee.com/deals/3" },
        ],
      },
      {
        title: "No activity in the past two weeks",
        deals: [
          { company: "HubSpot", companyLogo: "/logos/hubspot-color.svg", dealName: "HubSpot CRM Migration", dealUrl: "https://app.coffee.com/deals/4" },
          { company: "Shopify", companyLogo: "/logos/shopify.png", dealName: "Shopify Annual Contract", dealUrl: "https://app.coffee.com/deals/5" },
        ],
      },
      {
        title: "No next steps",
        deals: [
          { company: "Salesforce", companyLogo: "/logos/salesforce-color.svg", dealName: "Salesforce Integration", dealUrl: "https://app.coffee.com/deals/6" },
          { company: "Slack", companyLogo: "/logos/slack.svg", dealName: "Slack Enterprise Grid", dealUrl: "https://app.coffee.com/deals/7" },
          { company: "Zoom", companyLogo: "/logos/zoom.svg", dealName: "Zoom Workplace License", dealUrl: "https://app.coffee.com/deals/8" },
        ],
      },
      {
        title: "One or fewer linked contacts",
        deals: [
          { company: "Intercom", companyLogo: "/logos/intercom.svg", dealName: "Intercom Support Suite", dealUrl: "https://app.coffee.com/deals/9" },
          { company: "Zendesk", companyLogo: "/logos/zendesk.svg", dealName: "Zendesk Renewal", dealUrl: "https://app.coffee.com/deals/10" },
        ],
      },
    ],
    footerLink: { label: "Adjust your notification settings", url: "https://app.coffee.com/settings" },
  },
  "daily-prep": {
    subject: "Daily Prep from Coffee",
    preheader: "Your daily prep is ready.",
    heading: "Daily Prep",
    emoji: "☕",
    body: "Good morning, Doug. Here's your daily prep.",
    prepSections: [
      {
        title: "Today's External Meetings",
        meetings: [
          { time: "9:00 AM", company: "Stripe", companyLogo: "/logos/stripe.png", briefingUrl: "https://app.coffee.com/meetings/1" },
          { time: "10:30 AM", company: "HubSpot", companyLogo: "/logos/hubspot-color.svg", briefingUrl: "https://app.coffee.com/meetings/2" },
          { time: "1:00 PM", company: "Notion", companyLogo: "/logos/notion.svg", briefingUrl: "https://app.coffee.com/meetings/3" },
          { time: "3:30 PM", company: "Salesforce", companyLogo: "/logos/salesforce-color.svg", briefingUrl: "https://app.coffee.com/meetings/4" },
        ],
      },
      {
        title: "Action Items Due Today",
        tasks: [
          { title: "Send proposal to Stripe", taskUrl: "https://app.coffee.com/tasks/1" },
          { title: "Follow up with Maria at HubSpot", taskUrl: "https://app.coffee.com/tasks/2" },
          { title: "Review Notion contract terms", taskUrl: "https://app.coffee.com/tasks/3" },
          { title: "Prepare demo for Salesforce call", taskUrl: "https://app.coffee.com/tasks/4" },
        ],
      },
    ],
    footerLink: { label: "Adjust your notification settings", url: "https://app.coffee.com/settings" },
  },
  "daily-followups": {
    subject: "Your follow-up items from Coffee",
    preheader: "Your end of day wrap-up is ready.",
    heading: "Daily Follow-Ups",
    emoji: "🌅",
    body: "Hi Doug,\n\nHere's your end of day wrap-up.",
    followUpSections: [
      {
        title: "Meetings That Need Follow-Up",
        followUpMeetings: [
          { company: "Stripe", companyLogo: "/logos/stripe.png", followUpUrl: "https://app.coffee.com/compose/1" },
          { company: "Notion", companyLogo: "/logos/notion.svg", followUpUrl: "https://app.coffee.com/compose/2" },
          { company: "HubSpot", companyLogo: "/logos/hubspot-color.svg", followUpUrl: "https://app.coffee.com/compose/3" },
        ],
      },
      {
        title: "Suggested Tasks",
        suggestedTasks: [
          { title: "Send revised proposal to Stripe", addUrl: "https://app.coffee.com/tasks/add/1" },
          { title: "Share product roadmap with Notion", addUrl: "https://app.coffee.com/tasks/add/2" },
          { title: "Schedule follow-up demo with HubSpot", addUrl: "https://app.coffee.com/tasks/add/3" },
        ],
      },
      {
        title: "Suggested Deal Updates",
        suggestedDealUpdates: [
          { label: "Create new deal for Canva", actionLabel: "Add Deal", actionUrl: "https://app.coffee.com/deals/add/canva", isNew: true },
          { label: "Update close date for Datadog deal to 6/30/26", actionLabel: "Update Deal", actionUrl: "https://app.coffee.com/deals/datadog/edit" },
          { label: "Move Figma deal to Negotiation stage", actionLabel: "Update Deal", actionUrl: "https://app.coffee.com/deals/figma/edit" },
        ],
      },
    ],
    footerLink: { label: "Adjust your notification settings", url: "https://app.coffee.com/settings" },
  },
  "pipeline-changes": {
    subject: "Check out your pipeline changes in Coffee",
    preheader: "See what's changed in your pipeline this week.",
    heading: "Pipeline Changes",
    emoji: "💰",
    body: "Hi {{first_name}},\n\nCoffee allows you to easily see what's changed in your pipeline. This is what's changed between {{week_ago_date}} and {{today_date}}.",
    ctaLabel: "See My Comparison",
    ctaUrl: "https://app.coffee.com/deals?compare=true",
    table: [
      { label: "Stage changes", value: "12" },
      { label: "Deal size changes", value: "8" },
      { label: "Close date changes", value: "5" },
    ],
  },
  changelog: {
    subject: "What's new in Coffee — April 2026 ☕",
    preheader: "Website Visitor Tracking, Lead Finder, and Gong Integration.",
    heading: "What's New in Coffee",
    body: "Hi {{first_name}},\n\nWe've been busy roasting some powerful new features this month to help you identify prospects faster and manage your pipeline with even more precision. Here's what's brewing at Coffee.ai.\n\n☕ Visitor ID\nStop guessing who is browsing your site. Visitor ID tracks and analyzes your website traffic so you can see exactly who is showing interest in real-time. Even better, it sends live Slack notifications the moment a prospect lands on your page, allowing you to strike while the iron is hot.\n\n![Visitor ID](/brew-notes/visitor-id-april-2026.gif)\n\n☕ Lead Finder\nFind the right people faster. Lead Finder surfaces high-intent contacts that match your ICP from across the web, so your team always has a fresh, prioritized list of who to reach out to next.\n\n![Lead Finder](/brew-notes/lead-finder-april-2026.gif)\n\n☕ Gong Integration\nBring your Gong calls directly into Coffee. Recordings, transcripts, and AI summaries flow into the right deals and contacts automatically — no copy-paste, no context switching.\n\n![Gong Integration](/brew-notes/gong-integration-april-2026.gif)\n\nThat's your productivity shot for the month! ☕",
    ctaLabel: "See Full Brew Notes",
    ctaUrl: "https://www.coffee.ai/brew-notes",
  },
  "changelog-may": {
    subject: "What's new in Coffee ☕",
    preheader: "Suggested Leads, Credits, and Fiscal Year Support.",
    heading: "What's New in Coffee",
    body: "Hi {{first_name}},\n\nThis brew is all about helping you find the right leads faster and mapping Coffee to your exact sales process. Here's what's brewing at Coffee.ai.\n\n☕ Suggested Leads for Company Website Visitors\nVisitor Intelligence just got a major upgrade. When Coffee identifies a company visiting your site, we now surface the best contact persons at that company automatically — based on your buyer persona settings in Coffee's Intelligence section. Instead of seeing anonymous traffic and doing the research yourself, you can see the person most likely to be involved in the buying process and start a conversation faster.\n\n![Suggested Leads](https://www.coffee.ai/videos/brew-notes/visitor-id-slack-may-2026.mp4)\n\n☕ Introducing Credits\nEvery plan now includes a monthly credit allowance per seat. Credits power Coffee's AI agents: Meeting Summaries, Deal Updates, AI Fields, and Lead Imports. If your workspace runs through its monthly allowance, you can top up with a credit pack at any time. Purchased credits are shared among the workspace and are good for 12 months, so nothing goes to waste.\n\n![Credits](https://www.coffee.ai/videos/brew-notes/credits-billing-may-2026.mp4)\n\n☕ Fiscal Year Support\nIf your fiscal year doesn't line up with the calendar year, you can now set that in Coffee and have it reflected across all your reports and Ask Coffee queries. No more mental math when your Q1 starts in February.\n\n![Fiscal Year Support](https://www.coffee.ai/videos/brew-notes/fiscal-year-may-2026.mp4)\n\nAlso shipped in this brew: expanded Ask Coffee capabilities across the UI, Claude, and ChatGPT, plus a host of polish and performance improvements.\n\nThat's your productivity shot! ☕",
    ctaLabel: "See Full Brew Notes",
    ctaUrl: "https://www.coffee.ai/brew-notes",
  },
  "changelog-june": {
    subject: "What's new in Coffee — June 2026 ☕",
    preheader: "Meeting Clips, List Folders, and Formula Fields.",
    heading: "What's New in Coffee",
    body: "Hi {{first_name}},\n\nThis brew is all about shaping Coffee around the way your team actually works — your fields, your lists, your recordings. Here's what's brewing at Coffee.ai.\n\n☕ Meeting Clips\nThe recording tab got a full upgrade. Play, pause, skip ±15 seconds, mute, and change playback speed while the transcript auto-scrolls alongside the audio. Best of all, highlight any passage of the transcript and click Clip to create a shareable moment — perfect for sending a customer quote straight to your team.\n\n![Meeting Clips](https://www.coffee.ai/videos/brew-notes/meeting-clips-june-2026.mp4)\n\n☕ Formula Fields\nCustom fields can now calculate. Build SUM, AVERAGE, COUNT, LOOKUP, DATEDIF, and multi-condition IF/THEN formulas with a guided builder — pick your variables from your standard and custom fields, no syntax to memorize. Great for deal scoring, days-to-close, and health rollups.\n\n![Formula Fields](https://www.coffee.ai/videos/brew-notes/formula-fields-june-2026.mp4)\n\n☕ List Folders\nLists finally have somewhere to live. Organize static and dynamic lists into personal or shared folders so your team can find the right segment in seconds instead of scrolling a flat list.\n\n![List Folders](https://www.coffee.ai/videos/brew-notes/list-folders-june-2026.mp4)\n\nAlso shipped in this brew: Meetings and Notes are now separate sections with their own filters, reports can be downloaded as PDF or spreadsheet, workspace-wide grid view defaults for admins, Portuguese language support, and a long list of polish and performance improvements.\n\nThat's your productivity shot! ☕",
    ctaLabel: "See Full Brew Notes",
    ctaUrl: "https://www.coffee.ai/brew-notes",
  },
  "changelog-july": {
    subject: "What's new in Coffee — July 2026 ☕",
    preheader: "Enhanced Recording Panel, Deal Stage Criteria & Company Associations.",
    heading: "What's New in Coffee",
    body: "Hi {{first_name}},\n\nJuly's update is all about giving your team sharper tools to review, qualify, and map out the accounts that matter. Here's what's brewing.\n\n☕ Enhanced recording panel\nReviewing call recordings just got a lot more useful. Expand any recording into a side-by-side view with the transcript on the left and the video on the right — the transcript auto-scrolls and highlights the current line as you watch. Skip back or forward 15 seconds, speed playback up to 2.5x, and select any moment in the transcript to save it as a shareable clip.\n\n![Enhanced recording panel](/__l5e/assets-v1/6430ed77-f285-41b2-9104-a4503f9f4b4a/recording-panel-july-2026.gif)\n\n☕ Deal stage advance criteria\nSet required fields that must be filled in before a deal can move to a specific stage. No more deals sliding through your pipeline with missing info.\n\n![Deal stage advance criteria](/__l5e/assets-v1/8743d165-64e8-434a-bd6c-50cba0eedc6a/deal-stage-criteria-july-2026.gif)\n\n☕ Company associations\nLink companies together and define the relationship: Parent, Subsidiary, Partner or Other. Now your CRM reflects how accounts actually connect to each other, not just as standalone records.\n\n![Company associations](/__l5e/assets-v1/3573ecdc-22d6-4eb3-85b9-ed94d1a06c99/company-associations-july-2026.gif)\n\nAlso shipped\n• Data model in workspace settings: Custom fields now live in one central place. Data Model covers people, companies, deals, notes, and tasks all in one spot, so setting up and managing your custom fields is a whole lot easier.\n• Campaign engagement tracking: Campaigns now track opens, clicks, bounces, and unsubscribes, so you know exactly how your outreach is landing.\n• Visitor ID auto-add to lists: Website visitors can now be automatically added to company and people lists as they show up, so you can more easily track website visitors and add them to campaigns.\n\nThat's your productivity shot for the month! ☕",
    ctaLabel: "See Full Brew Notes",
    ctaUrl: "https://www.coffee.ai/brew-notes",
  },
  "changelog-august": {
    subject: "What's new in Coffee — August 2026 ☕",
    preheader: "Build Your Own Agent, Formula Fields & Web Forms.",
    heading: "What's New in Coffee",
    body: "Hi {{first_name}},\n\nLast month was all about giving you more control over how Coffee works for your team. Here's what's new. ☕\n\n☕ Build Your Own Agent\nDesign and configure your own AI agents inside Coffee to handle the workflows that matter most to your team. Pick the trigger, write the prompt, and choose where the results land — email, Slack, or right on the record.\n\n![Build Your Own Agent](/__l5e/assets-v1/6b22bcef-31e9-4da6-bc1c-11385c2bed4c/byo-agent-august-2026.gif)\n\n☕ Formula Fields\nBuild custom fields that calculate values automatically based on other data in your records. No more manual math or exporting to a spreadsheet just to get the number you need.\n\n![Formula Fields](/__l5e/assets-v1/a7c0fc0c-f2d4-4311-83c5-6f3de25f6940/formula-fields-august-2026.gif)\n\n☕ Web Forms\nCreate and embed forms directly from Coffee to capture leads and route them straight into your CRM, no extra tools required.\n\n![Web Forms](/__l5e/assets-v1/3e72b14f-db94-4299-985e-0206124f0a3f/web-forms-august-2026.gif)\n\nAlso shipped\n• Privacy settings: More granular control over who can see what. Set visibility at the record or field level so sensitive data stays with the right people.\n• Match filters for ICP and buyer persona: Sales intelligence and visitor identification now factor in match filters, so the contacts and companies surfaced are ranked against your defined ideal customer profile and buyer persona.\n• Read-only seats: Add teammates who need visibility into Coffee without edit access. Great for stakeholders who want to keep an eye on deals without the risk of an accidental change.\n\nThat's your productivity shot for the month! 💪",
    ctaLabel: "See Full Brew Notes",
    ctaUrl: "https://www.coffee.ai/brew-notes",
  },
  "not-synced-google": {
    subject: "You're missing out — please connect your Google Workspace account to Coffee",
    preheader: "Connect Google Workspace to unlock the full Coffee experience.",
    heading: "Connect Google Workspace",
    emoji: "🔗",
    icon: "/logos/google.svg",
    body: "Hi {{first_name}},\n\nWe noticed you haven't connected your Google account yet. This step is essential to get the most out of Coffee. Once connected, Coffee will:\n\n• Automatically create and enrich contacts and companies\n• Create AI briefings for upcoming meetings\n• Attach emails and meetings to records\n\nIt only takes 10 seconds to complete this step.",
    ctaLabel: "Connect Google Workspace",
    ctaUrl: "https://app.coffee.com/setup",
  },
  "not-synced-microsoft": {
    subject: "You're missing out — please connect your Microsoft 365 account to Coffee",
    preheader: "Connect Microsoft 365 to unlock the full Coffee experience.",
    heading: "Connect Microsoft 365",
    emoji: "🔗",
    icon: "/logos/microsoft.svg",
    body: "Hi {{first_name}},\n\nWe noticed you haven't connected your Microsoft 365 account yet. This step is essential to get the most out of Coffee. Once connected, Coffee will:\n\n• Automatically create and enrich contacts and companies\n• Create AI briefings for upcoming meetings\n• Attach emails and meetings to records\n\nIt only takes 10 seconds to complete this step.",
    ctaLabel: "Connect Microsoft 365",
    ctaUrl: "https://app.coffee.com/setup",
  },
  "no-meeting-recording": {
    subject: "See Coffee in Action — Record Your First Meeting",
    preheader: "Record your first meeting and unlock AI-powered insights.",
    heading: "Record Your First Meeting",
    emoji: "🎙️",
    body: "Hi {{first_name}},\n\nIt looks like you haven't recorded your first meeting in Coffee. Recording turns every meeting into actionable insights.\n\nEnable recording to gain access to:\n\n• AI meeting summaries\n• Detailed meeting notes\n• A shareable meeting recording and transcription\n• Draft of a follow-up email\n• Suggested action items\n• Suggested pipeline updates\n\nGive it a try with a meeting now so you can see what Coffee can really do.\n\nNot using video conferencing for this meeting? No problem — you can also try our [desktop recorder](https://app.coffee.com/settings?section=downloads) to capture in-person conversations, phone calls, and any audio on your computer. It's the easiest way to bring every conversation into Coffee.",
    ctaLabel: "Enable Recording",
    ctaUrl: "https://app.coffee.com/settings",
  },
  "no-deals": {
    subject: "Create your first deal in Coffee",
    preheader: "Unlock powerful insights and forecasting by adding your first deal.",
    heading: "Create Your First Deal",
    emoji: "💵",
    body: "Hi {{first_name}},\n\nWe noticed you haven't created a deal, imported deals, or connected a CRM yet. Adding deals unlocks powerful insights and forecasting. Deals are where the magic happens.\n\nOnce you add your first deal, connect your CRM, or import deals, Coffee will:\n\n• Let you view your deals in list or Kanban view\n• Start tracking all deal changes\n• Allow you to ask questions about your pipeline in Ask Coffee\n\nIf you need any assistance importing deals or connecting Salesforce or HubSpot, please feel free to reach out to us at [support@coffee.ai](mailto:support@coffee.ai).",
    ctaLabel: "Create a Deal",
    ctaUrl: "https://app.coffee.com/deals",
  },
};

/** Renders inline markdown links [text](url) within a string */
function renderTextWithLinks(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let lastIndex = 0;
  let match;
  while ((match = linkRegex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    parts.push(
      <a key={match.index} href={match[2]} target="_blank" rel="noopener noreferrer" className="text-primary underline">
        {match[1]}
      </a>
    );
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts;
}

function renderBulletLine(line: string, key: number) {
  const text = line.replace(/^•\s*/, "");

  return (
    <div key={key} className="my-1.5 flex items-start gap-2">
      <span className="text-foreground leading-6" aria-hidden="true">•</span>
      <p className="flex-1">{renderTextWithLinks(text)}</p>
    </div>
  );
}

function EmailTable({ rows }: { rows: { label: string; value: string | number }[] }) {
  return (
    <table className="w-full mt-4 mb-2" style={{ borderCollapse: "collapse" }}>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className={i < rows.length - 1 ? "border-b border-border" : ""}>
            <td className="py-2.5 text-muted-foreground" style={{ fontSize: 14 }}>{row.label}</td>
            <td className="py-2.5 text-right font-semibold text-foreground tabular-nums" style={{ fontSize: 14 }}>{row.value}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function EmailBody({ email }: { email: EmailMeta }) {
  const { t } = useTranslation();
  const imageRegex = /^!\[([^\]]*)\]\(([^)]+)\)$/;

  return (
    <>
      {email.body.split("\n\n").map((paragraph, i) => (
        <div key={i}>
          {paragraph.split("\n").map((line, j) => {
            // Inline image syntax: ![alt](url)
            const imgMatch = line.match(imageRegex);
            if (imgMatch) {
              const url = imgMatch[2];
              const isVideo = /\.(mp4|webm)$/i.test(url);
              if (isVideo) {
                return (
                  <div key={j} className="my-3 rounded-md overflow-hidden border border-border">
                    <video
                      src={url}
                      autoPlay
                      loop
                      muted
                      playsInline
                      className="w-full h-auto"
                      aria-label={imgMatch[1]}
                    />
                  </div>
                );
              }
              return (
                <div key={j} className="my-3 rounded-md overflow-hidden border border-border">
                  <img src={url} alt={imgMatch[1]} className="w-full h-auto" />
                </div>
              );
            }
            if (line.startsWith("• ")) {
              return renderBulletLine(line, j);
            }
            if (/^[☕🚀🔧🐛]/.test(line)) {
              return (
                <p key={j} className="font-semibold text-foreground mt-3 mb-1">
                  {line}
                </p>
              );
            }
            return <p key={j}>{renderTextWithLinks(line)}</p>;
          })}
        </div>
      ))}
      {email.table && <EmailTable rows={email.table} />}
      {email.dealSections && email.dealSections.map((section, si) => (
        <div key={si} className={si === 0 ? "mt-5" : "mt-16"}>
          <p className="font-semibold text-foreground text-sm mb-2">{section.title}</p>
          <div className="space-y-1.5">
            {section.deals.map((deal, di) => (
              <div key={di} className="flex items-center gap-2">
                <span className="text-foreground" aria-hidden="true">•</span>
                {deal.companyLogo && (
                  <img
                    src={deal.companyLogo}
                    alt={deal.company}
                    className="h-4 w-4 rounded-sm object-contain shrink-0"
                    onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                  />
                )}
                {deal.dealUrl ? (
                  <a href={deal.dealUrl} className="text-sm text-primary underline">{deal.dealName}</a>
                ) : (
                  <span className="text-sm">{deal.dealName}</span>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      {email.prepSections && email.prepSections.map((section, si) => (
        <div key={`prep-${si}`} className={si === 0 ? "mt-5" : "mt-16"}>
          <p className="font-semibold text-foreground text-sm mb-2">{section.title}</p>
          {section.meetings && (
            <div className="space-y-2">
              {section.meetings.map((m, mi) => (
                <div key={mi} className="flex items-center gap-2.5">
                  <span className="text-xs text-muted-foreground font-medium tabular-nums w-16 shrink-0">{m.time}</span>
                  {m.companyLogo && (
                    <img
                      src={m.companyLogo}
                      alt={m.company}
                      className="h-4 w-4 rounded-sm object-contain shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <span className="text-sm text-foreground">{m.company}</span>
                  <a href={m.briefingUrl} className="text-xs text-primary underline ml-auto shrink-0">{t("wizards.emailPreviewViewBriefing")}</a>
                </div>
              ))}
            </div>
          )}
          {section.tasks && (
            <div className="space-y-1.5">
              {section.tasks.map((task, ti) => (
                <div key={ti} className="flex items-center gap-2">
                  <span className="text-foreground" aria-hidden="true">•</span>
                  <a href={task.taskUrl} className="text-sm text-primary underline">{task.title}</a>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {email.followUpSections && email.followUpSections.map((section, si) => (
        <div key={`followup-${si}`} className={si === 0 ? "mt-5" : "mt-16"}>
          <p className="font-semibold text-foreground text-sm mb-2">{section.title}</p>
          {section.followUpMeetings && (
            <div className="space-y-1.5">
              {section.followUpMeetings.map((m, mi) => (
                <div key={mi} className="flex items-center gap-2">
                  <span className="text-foreground" aria-hidden="true">•</span>
                  {m.companyLogo && (
                    <img
                      src={m.companyLogo}
                      alt={m.company}
                      className="h-4 w-4 rounded-sm object-contain shrink-0"
                      onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
                    />
                  )}
                  <span className="text-sm">{m.company}</span>
                  <a href={m.followUpUrl} className="text-xs text-primary underline ml-auto shrink-0">{t("wizards.emailPreviewOpenFollowUp")}</a>
                </div>
              ))}
            </div>
          )}
          {section.suggestedTasks && (
            <div className="space-y-1.5">
              {section.suggestedTasks.map((task, ti) => (
                <div key={ti} className="flex items-center gap-2">
                  <span className="text-foreground" aria-hidden="true">•</span>
                  <span className="text-sm flex-1">{task.title}</span>
                  <a href={task.addUrl} className="text-xs text-primary underline shrink-0">{t("wizards.emailPreviewAddTask")}</a>
                </div>
              ))}
            </div>
          )}
          {section.suggestedDealUpdates && (
            <div className="space-y-1.5">
              {section.suggestedDealUpdates.map((d, di) => (
                <div key={di} className="flex items-center gap-2">
                  <span className="text-foreground" aria-hidden="true">{d.isNew ? "+" : "↻"}</span>
                  <span className="text-sm flex-1">{d.label}</span>
                  <a href={d.actionUrl} className="text-xs text-primary underline shrink-0">{d.actionLabel}</a>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
      {email.footerLink && (
        <div className="mt-6 text-center">
          <a href={email.footerLink.url} className="text-xs text-primary underline">
            {email.footerLink.label}
          </a>
        </div>
      )}
    </>
  );
}

function EmailTemplate({ type, viewport }: { type: CustomerEmailType; viewport: "desktop" | "mobile" }) {
  const { t } = useTranslation();
  const email = EMAIL_DATA[type];
  const isMobile = viewport === "mobile";

  return (
    <div
      className="bg-muted/30 rounded-lg overflow-hidden border border-border mx-auto"
      style={{ maxWidth: isMobile ? 375 : undefined }}
    >
      {/* Email client chrome */}
      <div className="bg-muted/50 px-4 py-2 border-b border-border flex items-center gap-2 text-xs text-muted-foreground">
        <span className="font-medium text-foreground">{t("wizards.emailPreviewSubject")}</span>
        <span className="truncate">{email.subject}</span>
      </div>

      {/* Email body */}
      <div className="bg-background" style={{ fontFamily: "'Helvetica Neue', Helvetica, Arial, sans-serif" }}>
        {/* Top accent bar */}
        <div className="h-1 bg-foreground" />

        <div
          className="mx-auto"
          style={{
            maxWidth: isMobile ? 375 : 560,
            padding: isMobile ? "24px 20px" : "40px 32px",
          }}
        >
          {/* Logo + Emoji + Heading area */}
          {type === "welcome" ? (
            <>
              {/* Coffee Logo */}
              <div className="text-center" style={{ marginBottom: isMobile ? 8 : 12 }}>
                <img
                  src="/logos/coffee-icon-black.png"
                  alt="Coffee"
                  className="mx-auto dark:invert"
                  style={{ height: isMobile ? 32 : 40, width: isMobile ? 32 : 40 }}
                />
              </div>
              {/* Emoji */}
              {email.emoji && (
                <div className="text-center" style={{ fontSize: isMobile ? 28 : 36, marginBottom: isMobile ? 8 : 12 }}>
                  {email.emoji}
                </div>
              )}
              {/* Heading */}
              <h1
                className="font-semibold text-foreground text-center"
                style={{ fontSize: isMobile ? 18 : 20 }}
              >
                {email.heading}
              </h1>
            </>
          ) : (
            <>
              {/* Coffee Logo */}
              <div className="text-center" style={{ marginBottom: isMobile ? 8 : 12 }}>
                <img
                  src="/logos/coffee-icon-black.png"
                  alt="Coffee"
                  className="mx-auto dark:invert"
                  style={{ height: isMobile ? 32 : 40, width: isMobile ? 32 : 40 }}
                />
              </div>

              {/* Emoji below coffee logo */}
              {email.emoji && (
                <div className="text-center" style={{ fontSize: isMobile ? 28 : 36, marginBottom: isMobile ? 8 : 12 }}>
                  {email.emoji}
                </div>
              )}

              {/* Icon for integration emails (when no emoji) */}
              {!email.emoji && email.icon && (
                <div className="text-center" style={{ marginBottom: isMobile ? 16 : 24 }}>
                  <img
                    src={email.icon}
                    alt=""
                    className="mx-auto object-contain"
                    style={{ height: isMobile ? 32 : 40, width: isMobile ? 32 : 40 }}
                  />
                </div>
              )}

              {/* Heading */}
              <h1
                className="font-semibold text-foreground text-center"
                style={{
                  fontSize: isMobile ? 18 : 20,
                  marginBottom: isMobile ? 16 : 24,
                }}
              >
                {email.heading}
              </h1>
            </>
          )}

          {/* Body */}
          <div
            className="text-muted-foreground leading-relaxed space-y-3"
            style={{ fontSize: isMobile ? 13 : 14 }}
          >
            <EmailBody email={email} />
          </div>

          {/* CTA Button */}
          {email.ctaLabel && (
            <div className="text-center" style={{ marginTop: isMobile ? 24 : 32 }}>
              <a
                href={email.ctaUrl}
                className="inline-block rounded-md bg-foreground text-background font-medium no-underline"
                style={{
                  padding: isMobile ? "10px 20px" : "12px 24px",
                  fontSize: isMobile ? 13 : 14,
                }}
              >
                {email.ctaLabel}
              </a>
            </div>
          )}

          {/* Footer */}
          <div
            className="border-t border-border text-center"
            style={{
              marginTop: isMobile ? 28 : 40,
              paddingTop: isMobile ? 16 : 24,
            }}
          >
            <p className="text-xs text-muted-foreground">
              {t("wizards.emailPreviewCopyright")}
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              <a href="#" className="underline">{t("wizards.emailPreviewUnsubscribe")}</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

interface CustomerEmailPreviewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  emailType: CustomerEmailType;
}

export default function CustomerEmailPreview({ open, onOpenChange, emailType }: CustomerEmailPreviewProps) {
  const { t } = useTranslation();
  const email = EMAIL_DATA[emailType];
  const [viewport, setViewport] = useState<"desktop" | "mobile">("desktop");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[780px] max-h-[90vh] overflow-y-auto p-0">
        <DialogHeader className="px-6 pt-6 pb-0 flex flex-row items-center justify-between">
          <DialogTitle className="text-base">
            {t("wizards.emailPreviewTitle", { heading: email.heading })}
          </DialogTitle>
          <div className="flex items-center gap-1 border border-border rounded-md p-0.5">
            <Button
              size="sm"
              variant={viewport === "desktop" ? "secondary" : "ghost"}
              className="h-7 w-7 p-0"
              onClick={() => setViewport("desktop")}
            >
              <Monitor className="h-3.5 w-3.5" />
            </Button>
            <Button
              size="sm"
              variant={viewport === "mobile" ? "secondary" : "ghost"}
              className="h-7 w-7 p-0"
              onClick={() => setViewport("mobile")}
            >
              <Smartphone className="h-3.5 w-3.5" />
            </Button>
          </div>
        </DialogHeader>
        <div className="px-6 pb-6">
          <EmailTemplate type={emailType} viewport={viewport} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { EMAIL_DATA };
